import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workspaceApi } from '../api/apiClient';
import './RecruitmentAssistantPage.css';

function AssistantCard({ title, description, cta, onClick }) {
  return (
    <button type="button" className="v-ra-card" onClick={onClick}>
      <h3>{title}</h3>
      <p>{description}</p>
      <span className="v-ra-card__cta">{cta}</span>
    </button>
  );
}

export default function RecruitmentAssistantPage() {
  const navigate = useNavigate();
  const [workspaces, setWorkspaces] = useState([]);
  const [workspaceId, setWorkspaceId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await workspaceApi.list();
        const rows = Array.isArray(res.data) ? res.data : [];
        if (!cancelled) {
          setWorkspaces(rows);
          if (rows.length > 0) {
            setWorkspaceId(String(rows[0].id));
          }
        }
      } catch (e) {
        if (!cancelled) {
          setError('Unable to load workspaces right now. Please try again.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedWorkspace = useMemo(
    () => workspaces.find((w) => String(w.id) === String(workspaceId)) || null,
    [workspaces, workspaceId]
  );

  const hasWorkspace = Boolean(selectedWorkspace);

  if (loading) {
    return <div className="v-loading">Loading workspace assistant...</div>;
  }

  return (
    <div className="v-ra-page">
      <div className="v-page__header">
        <div>
          <h1 className="v-page__title">Workspace Assistant</h1>
          <p className="v-page__subtitle">
            Open Search, Document Chat, and Agenda for a selected workspace.
          </p>
        </div>
      </div>

      {error && <div className="v-ra-alert">{error}</div>}

      <div className="v-ra-picker">
        <label htmlFor="ra-workspace">Workspace</label>
        <select
          id="ra-workspace"
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          disabled={workspaces.length === 0}
        >
          {workspaces.length === 0 ? (
            <option value="">No workspaces available</option>
          ) : (
            workspaces.map((ws) => (
              <option key={ws.id} value={String(ws.id)}>
                {ws.name}
              </option>
            ))
          )}
        </select>
      </div>

      <div className="v-ra-grid">
        <AssistantCard
          title="Open search"
          description="Keyword search across file names and indexed chunks (no AI)."
          cta="Open search"
          onClick={() => hasWorkspace && navigate(`/workspaces/${workspaceId}/search`)}
        />

        <AssistantCard
          title="Document Chat"
          description="Ask questions against uploaded files and receive cited, auditable answers."
          cta="Open chat"
          onClick={() => hasWorkspace && navigate(`/workspaces/${workspaceId}/qa`)}
        />

        <AssistantCard
          title="Agenda & reminders"
          description="See all agenda items from Q&A in one place and track what is due soon."
          cta="Open agenda"
          onClick={() => hasWorkspace && navigate(`/workspaces/${workspaceId}/agenda`)}
        />
      </div>
    </div>
  );
}
